import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogOut, Heart, Ticket, Calendar, Instagram, Twitter, Linkedin, Gift, Copy, Sparkles } from "lucide-react";
import { PhotoUploader, AvatarUploader } from "@/components/PhotoUploader";

const SOCIAL_FIELDS = [
  { key: "instagram", label: "Instagram", placeholder: "@yourhandle", icon: Instagram, color: "text-pink-400" },
  { key: "tiktok", label: "TikTok", placeholder: "@yourhandle", iconText: "T", color: "text-white" },
  { key: "x", label: "X (Twitter)", placeholder: "@yourhandle", icon: Twitter, color: "text-slate-200" },
  { key: "linkedin", label: "LinkedIn", placeholder: "profile-name", icon: Linkedin, color: "text-blue-400" },
  { key: "snapchat", label: "Snapchat", placeholder: "yourhandle", iconText: "S", color: "text-yellow-300" },
];

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    bio: "",
    city: "",
    age: "",
    photo_url: "",
    dating_bio: "",
    dating_photos: [],
    dating_interests_text: "",
    dating_enabled: false,
    social_handles: {},
  });
  const [busy, setBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [hosted, setHosted] = useState([]);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        city: user.city || "",
        age: user.age?.toString() || "",
        photo_url: user.photo_url || "",
        dating_bio: user.dating_bio || "",
        dating_photos: user.dating_photos || [],
        dating_interests_text: (user.dating_interests || []).join(", "),
        dating_enabled: !!user.dating_enabled,
        social_handles: user.social_handles || {},
      });
    }
    (async () => {
      try {
        const [t, h, r] = await Promise.all([
          api.get("/tickets/mine"),
          api.get("/events/mine/hosted"),
          api.get("/referrals/me"),
        ]);
        setTickets(t.data);
        setHosted(h.data);
        setRef(r.data);
      } catch (err) {
        console.error("[Profile] failed to load profile data", err);
      }
    })();
  }, [user]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const age = form.age ? parseInt(form.age) : null;
    if (form.dating_enabled && (!age || age < 18)) {
      toast.error("You must be 18+ to use dating. Set your age first.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        bio: form.bio,
        city: form.city,
        age: age,
        photo_url: form.photo_url || null,
        dating_bio: form.dating_bio,
        dating_photos: (form.dating_photos || []).filter(Boolean),
        dating_interests: form.dating_interests_text.split(",").map((s) => s.trim()).filter(Boolean),
        dating_enabled: form.dating_enabled,
        social_handles: form.social_handles || {},
      };
      await api.patch("/auth/me", payload);
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-32">
      <TopBar
        right={
          <button onClick={doLogout} data-testid="profile-logout" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-400">
            <LogOut size={14} /> Sign out
          </button>
        }
      />

      <main className="max-w-2xl mx-auto pt-20 px-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center text-2xl font-display font-black overflow-hidden">
            {user.photo_url ? <img src={user.photo_url} alt="" className="w-full h-full object-cover" /> : user.name[0].toUpperCase()}
          </div>
          <div>
            <div className="overline text-slate-500">YOUR PROFILE</div>
            <h1 className="font-display font-black text-2xl tracking-tight">{user.name}</h1>
            <div className="text-xs text-slate-500">{user.email}</div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard testid="stat-tickets" icon={Ticket} label="Tickets" value={tickets.length} color="rose" />
          <StatCard testid="stat-hosted" icon={Calendar} label="Hosted" value={hosted.length} color="amber" />
          <StatCard testid="stat-credit" icon={Gift} label="Credit" value={`$${(ref?.credit_balance || 0).toFixed(0)}`} color="emerald" />
        </div>

        {ref?.referral_code && (
          <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-rose-500/15 via-amber-500/10 to-violet-500/15 border border-amber-500/25" data-testid="referral-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-amber-400" />
              <h3 className="font-display font-bold text-lg">Invite friends, both earn ${ref.reward_per_referral.toFixed(0)}</h3>
            </div>
            <p className="text-sm text-slate-300 mb-4">Share your code. When they buy their first ticket, you both get ${ref.reward_per_referral.toFixed(0)} in credit automatically.</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 px-4 h-11 rounded-xl bg-black/40 border border-white/10 font-mono font-bold text-lg tracking-widest flex items-center" data-testid="referral-code">
                {ref.referral_code}
              </div>
              <button
                data-testid="referral-copy-code"
                onClick={() => {
                  navigator.clipboard.writeText(ref.referral_code);
                  toast.success("Code copied");
                }}
                className="h-11 px-4 rounded-xl bg-white/10 hover:bg-white/15 text-sm font-semibold inline-flex items-center gap-1.5"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
            <button
              data-testid="referral-copy-link"
              onClick={() => {
                const link = `${window.location.origin}/auth?ref=${ref.referral_code}`;
                navigator.clipboard.writeText(link);
                toast.success("Invite link copied");
              }}
              className="w-full h-11 rounded-xl bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5"
            >
              <Copy size={14} /> Copy invite link
            </button>
            {ref.referrals?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="overline text-slate-400 mb-2">YOUR INVITES</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {ref.referrals.map((r) => (
                    <div key={r.invitee_id || `${r.inviter_id}-${r.invitee_name}`} className="flex justify-between text-sm">
                      <span className="text-slate-300">{r.invitee_name}</span>
                      <span className={`font-mono text-xs px-2 py-0.5 rounded-full ${r.status === "converted" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>
                        {r.status === "converted" ? `+$${(r.reward || 0).toFixed(0)}` : "PENDING"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-5">
          <Section title="Basics">
            <Field label="Profile photo">
              <AvatarUploader url={form.photo_url} onChange={(u) => set("photo_url", u)} testid="profile-avatar" />
            </Field>
            <Field label="Display name"><Input data-testid="profile-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#131A26] border-white/10 h-11" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City"><Input data-testid="profile-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-[#131A26] border-white/10 h-11" /></Field>
              <Field label="Age"><Input data-testid="profile-age" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="bg-[#131A26] border-white/10 h-11" /></Field>
            </div>
            <Field label="Bio"><Textarea data-testid="profile-bio" rows={2} value={form.bio} onChange={(e) => set("bio", e.target.value)} className="bg-[#131A26] border-white/10" /></Field>
          </Section>

          <Section title="Dating profile" icon={<Heart size={16} className="text-rose-400" />}>
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs text-amber-200 flex items-start gap-2" data-testid="dating-age-notice">
              <Sparkles size={14} className="shrink-0 mt-0.5" />
              <span>You must be <b>18 or older</b> to use the dating features. Set your age above before enabling.</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-[#131A26] border border-white/10">
              <div>
                <div className="font-semibold text-sm">Show me on Match</div>
                <div className="text-xs text-slate-400">Turn on to appear in the dating deck</div>
              </div>
              <Switch
                checked={form.dating_enabled}
                disabled={!form.age || parseInt(form.age) < 18}
                onCheckedChange={(v) => {
                  if (v && (!form.age || parseInt(form.age) < 18)) {
                    toast.error("Set your age to 18 or older first.");
                    return;
                  }
                  set("dating_enabled", v);
                }}
                data-testid="dating-toggle"
              />
            </div>
            <Field label="Dating bio"><Textarea data-testid="profile-dating-bio" rows={3} value={form.dating_bio} onChange={(e) => set("dating_bio", e.target.value)} placeholder="One line that sums you up..." className="bg-[#131A26] border-white/10" /></Field>
            <Field label="Photos (tap to upload — up to 6)">
              <PhotoUploader
                photos={form.dating_photos}
                onChange={(p) => set("dating_photos", p)}
                max={6}
                testIdPrefix="dating-photo"
              />
            </Field>
            <Field label="Interests (comma separated)"><Input data-testid="profile-dating-interests" value={form.dating_interests_text} onChange={(e) => set("dating_interests_text", e.target.value)} placeholder="coffee, techno, hiking" className="bg-[#131A26] border-white/10 h-11" /></Field>
          </Section>

          <Section title="Social handles">
            {SOCIAL_FIELDS.map((s) => (
              <Field key={s.key} label={s.label}>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${s.color}`}>
                    {s.icon ? <s.icon size={16} /> : <span className="font-bold text-xs">{s.iconText}</span>}
                  </span>
                  <Input
                    data-testid={`social-${s.key}`}
                    value={form.social_handles?.[s.key] || ""}
                    onChange={(e) => setForm((p) => ({ ...p, social_handles: { ...p.social_handles, [s.key]: e.target.value } }))}
                    placeholder={s.placeholder}
                    className="bg-[#131A26] border-white/10 h-11 pl-10"
                  />
                </div>
              </Field>
            ))}
          </Section>

          <Button onClick={save} disabled={busy} data-testid="profile-save" className="w-full h-12 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl">
            {busy ? "Saving..." : "Save profile"}
          </Button>
        </div>

        {/* My tickets */}
        {tickets.length > 0 && (
          <div className="mt-8">
            <h2 className="overline text-rose-400 mb-3">MY TICKETS</h2>
            <div className="space-y-2">
              {tickets.map((t) => (
                <div key={t.id} data-testid={`ticket-${t.id}`} className="p-3 rounded-xl bg-[#131A26] border border-white/10 flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-sm">{t.event_title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Paid ${t.amount.toFixed(2)}</div>
                  </div>
                  <span className="overline px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px]">CONFIRMED</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="overline text-slate-400">{label}</Label>
    {children}
  </div>
);

const Section = ({ title, icon, children }) => (
  <div className="space-y-3 p-4 rounded-2xl bg-[#0F1520] border border-white/8">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="font-display font-bold text-lg">{title}</h3>
    </div>
    {children}
  </div>
);

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className={`p-4 rounded-xl bg-${color}-500/10 border border-${color}-500/25`}>
    <Icon size={18} className={`text-${color}-400 mb-2`} />
    <div className="font-display font-black text-2xl">{value}</div>
    <div className="text-xs text-slate-400">{label}</div>
  </div>
);
